import { useState, type ReactNode } from 'react';
import classNames from 'classnames';
import Link from 'next/link';

import Icon from '@utilities/icon';
import { colors } from '@configs/Theme';
import Divider from '@com/_atoms/Divider';
import NextImage from '@com/_core/NextImage';
import { colorPallete } from '@configs/color-pallete';

import {
  ConsultantTypeEnum,
  DoctorActivityStatusEnum,
} from '@models/ocs/doctor';

import { DoctorCardModel, DoctorListTypeEnum } from '@models/doctor-list';
import { routeList } from '@routes/routeList';

import ConsultantType from './consultant-type';

type DoctorCardProps = {
  item: DoctorCardModel;
  profileHref?: string;
  onClick?: () => void;
  isLoading?: boolean;
  doctorListType?: DoctorListTypeEnum;
  isJustNobatButton?: boolean;
  hideActivityStatus?: boolean;
  buttonComponent?: (params: {
    routeLoadingId: number | null;
    setRouteLoadingId: (id: number | null) => void;
  }) => ReactNode;
};

const DoctorCard = ({
  item,
  profileHref,
  onClick,
  doctorListType,
  hideActivityStatus = false,
  buttonComponent,
}: DoctorCardProps) => {
  const [routeLoadingId, setRouteLoadingId] = useState<number | null>(null);
  const isNobatList = doctorListType === DoctorListTypeEnum.NOBAT;
  const showActivityStatus =
    !hideActivityStatus && !isNobatList && item?.activityStatus !== undefined;
  const isOnline =
    showActivityStatus &&
    item?.activityStatus !== DoctorActivityStatusEnum.Offline;

  const resolvedProfileHref =
    profileHref ?? `${routeList.doctorProfile}/${item?.recId}`;

  const profileContent = (
    <>
      <div className="h-full flex items-center gap-x-2 relative pt-1 pb-1.5">
        <div className="relative">
          <div
            className={classNames(
              'w-[56px] h-[56px] flex items-center justify-center rounded-full overflow-hidden border',
              {
                'border-border-primary': !showActivityStatus,
                'border-green-300': showActivityStatus && isOnline,
                'border-surface-tertiary': showActivityStatus && !isOnline,
              },
            )}
          >
            <NextImage
              src={item?.imageUrl ?? '/images/ocs/images/profile.svg'}
              alt={item?.fullName || item?.title || item?.doctorName || 'doctor'}
              width={56}
              height={56}
            />
          </div>
          {showActivityStatus && (
            <div className="absolute top-2 right-2 p-[2px] flex items-center justify-center rounded-full bg-surface-primary translate-x-1/2 -translate-y-1/2">
              <span className="relative flex w-[8px] h-[8px]">
                {isOnline && (
                  <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping"></span>
                )}
                <span
                  className={`w-[8px] h-[8px] rounded-full ${
                    isOnline ? 'bg-green-300' : 'bg-surface-tertiary'
                  }`}
                />
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col">
          <span className="text-Label/xSmall text-content-primary">
            {item?.fullName || item?.title || item?.doctorName}
          </span>
          <span className="text-Body/xSmall text-content-tertiary">
            {item?.latestSpecialityName ||
              item?.specialityName ||
              item?.subtitle}
          </span>
          {!!item?.cities?.length && (
            <div className="flex items-center gap-x-1">
              <div>
                <Icon
                  name="PinCircle"
                  width={1}
                  height={1}
                  fill={colorPallete?.content?.tertiary}
                />
              </div>

              <span className="text-Label/2xSmall text-content-tertiary">
                {item?.cities?.join('- ')}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-x-2 px-2 py-1 bg-surface-secondary rounded-md">
        <div className="w-full flex items-center gap-x-1 justify-center">
          <Icon
            name="StarFill"
            width={1}
            height={1}
            fill={colors.yellow[400]}
          />
          {!!item?.rate && (
            <>
              <span className="text-Label/2xSmall text-content-primary">
                {item?.rate}
              </span>

              <span className="text-Label/2xSmall text-content-primary">
                {!!item?.doctorReviewsCount &&
                  `(از ${item?.doctorReviewsCount} نظر)`}
              </span>
            </>
          )}

          {!item?.rate && (
            <span className="text-Label/2xSmall text-content-primary">
              بدون نظر
            </span>
          )}
        </div>

        <div className="h-4 w-1 border-l border-border-primary"></div>

        <ConsultantType
          type={ConsultantTypeEnum.RX}
          iconColor={colorPallete.content.accent}
          className="w-full"
        />
      </div>
    </>
  );

  return (
    <article className="h-full flex flex-col p-3 gap-y-2 shadow-fab rounded-md bg-surface-primary">
      {onClick ? (
        <button
          type="button"
          className="w-full text-right"
          onClick={onClick}
        >
          {profileContent}
        </button>
      ) : (
        <Link href={resolvedProfileHref} className="w-full text-right">
          {profileContent}
        </Link>
      )}

      <Divider className="h-[1px] bg-surface-secondary" padding={0} />
      <div className="flex items-center justify-between gap-x-4">
        {buttonComponent?.({ routeLoadingId, setRouteLoadingId })}
      </div>
    </article>
  );
};

export default DoctorCard;
